import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    TimesIcon,
    SearchIcon,
    CheckCircleIcon,
    TrashIcon,
    UndoIcon,
    ExpandIcon,
    CompressIcon,
    CrosshairsIcon
} from '@patternfly/react-icons';
import {
    GoogleMap,
    LoadScript,
    Marker,
    Polyline,
    Autocomplete
} from '@react-google-maps/api';
import {
    DrawRoutesConfig,
    LatLng,
    RouteWaypoint,
    DEFAULT_STARTING_POINT,
    ROUTE_DRAWING_ZOOM,
    MIN_WAYPOINTS,
    MAX_WAYPOINTS
} from './types/drawRoutes';
import { SOCAL_RESTRICTION, SOCAL_AUTOCOMPLETE_BOUNDS, isWithinSoCalBounds } from './types/mapConstants';

// Google Maps libraries to load
const libraries: ("places")[] = ["places"];

// Get API key from environment
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export interface IDrawRouteMapModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (config: DrawRoutesConfig) => void;
    initialConfig?: DrawRoutesConfig;
}

const DrawRouteMapModal: React.FunctionComponent<IDrawRouteMapModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialConfig
}) => {
    // Starting point state
    const [startingAddress, setStartingAddress] = useState(initialConfig?.startingPoint?.address || '');
    const [startingLat, setStartingLat] = useState(initialConfig?.startingPoint?.latitude || DEFAULT_STARTING_POINT.latitude);
    const [startingLng, setStartingLng] = useState(initialConfig?.startingPoint?.longitude || DEFAULT_STARTING_POINT.longitude);
    const [startingPlaceId, setStartingPlaceId] = useState(initialConfig?.startingPoint?.placeId || '');
    const [hasStartingPoint, setHasStartingPoint] = useState(!!initialConfig?.startingPoint?.address);

    // Waypoints and route state
    const [waypoints, setWaypoints] = useState<RouteWaypoint[]>(initialConfig?.waypoints || []);
    const [snappedPath, setSnappedPath] = useState<LatLng[]>(initialConfig?.snappedPath || []);
    const [totalDistanceMeters, setTotalDistanceMeters] = useState(initialConfig?.totalDistanceMeters || 0);
    const [totalDistanceText, setTotalDistanceText] = useState(initialConfig?.totalDistanceText || '');
    const [isValidated, setIsValidated] = useState(initialConfig?.isValidated || false);

    // UI state
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLocating, setIsLocating] = useState(false);

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);

    // Reset state when modal opens with new initial values
    useEffect(() => {
        if (isOpen) {
            if (initialConfig) {
                setStartingAddress(initialConfig.startingPoint?.address || '');
                setSearchInput(initialConfig.startingPoint?.address || '');
                setStartingLat(initialConfig.startingPoint?.latitude || DEFAULT_STARTING_POINT.latitude);
                setStartingLng(initialConfig.startingPoint?.longitude || DEFAULT_STARTING_POINT.longitude);
                setStartingPlaceId(initialConfig.startingPoint?.placeId || '');
                setHasStartingPoint(!!initialConfig.startingPoint?.address);
                setWaypoints(initialConfig.waypoints || []);
                setSnappedPath(initialConfig.snappedPath || []);
                setTotalDistanceMeters(initialConfig.totalDistanceMeters || 0);
                setTotalDistanceText(initialConfig.totalDistanceText || '');
                setIsValidated(initialConfig.isValidated || false);
            } else {
                resetState();
            }
        }
    }, [isOpen, initialConfig]);

    const resetState = () => {
        setStartingAddress('');
        setSearchInput('');
        setStartingLat(DEFAULT_STARTING_POINT.latitude);
        setStartingLng(DEFAULT_STARTING_POINT.longitude);
        setStartingPlaceId('');
        setHasStartingPoint(false);
        setWaypoints([]);
        setSnappedPath([]);
        setTotalDistanceMeters(0);
        setTotalDistanceText('');
        setIsValidated(false);
        setIsFullscreen(false);
    };

    // Initialize directions service when map loads
    const handleMapLoad = useCallback((mapInstance: google.maps.Map) => {
        setMap(mapInstance);
        directionsServiceRef.current = new google.maps.DirectionsService();
    }, []);

    // Handle place selection from autocomplete
    const handlePlaceChanged = useCallback(() => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const formattedAddress = place.formatted_address || place.name || '';
                const placeId = place.place_id || '';

                setStartingLat(lat);
                setStartingLng(lng);
                setStartingAddress(formattedAddress);
                setSearchInput(formattedAddress);
                setStartingPlaceId(placeId);
                setHasStartingPoint(true);

                // Clear existing route when starting point changes
                setWaypoints([]);
                setSnappedPath([]);
                setTotalDistanceMeters(0);
                setTotalDistanceText('');
                setIsValidated(false);

                // Pan and zoom map to location
                if (map) {
                    map.panTo({ lat, lng });
                    map.setZoom(ROUTE_DRAWING_ZOOM);
                }
            }
        }
    }, [map]);

    // Handle manual address search
    const handleAddressSearch = useCallback((searchAddress: string) => {
        if (!searchAddress.trim()) return;

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: searchAddress }, (results, status) => {
            if (status === 'OK' && results && results[0] && results[0].geometry) {
                const location = results[0].geometry.location;
                const lat = location.lat();
                const lng = location.lng();
                const formattedAddress = results[0].formatted_address || searchAddress;
                const placeId = results[0].place_id || '';

                setStartingLat(lat);
                setStartingLng(lng);
                setStartingAddress(formattedAddress);
                setSearchInput(formattedAddress);
                setStartingPlaceId(placeId);
                setHasStartingPoint(true);

                // Clear existing route
                setWaypoints([]);
                setSnappedPath([]);
                setTotalDistanceMeters(0);
                setTotalDistanceText('');
                setIsValidated(false);

                if (map) {
                    map.panTo({ lat, lng });
                    map.setZoom(ROUTE_DRAWING_ZOOM);
                }
            }
        });
    }, [map]);

    // Handle Enter key in search input
    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddressSearch(searchInput);
        }
    }, [searchInput, handleAddressSearch]);

    // Use browser geolocation to set starting point
    const handleUseCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) return;
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                if (!isWithinSoCalBounds(lat, lng)) {
                    console.warn('Current location is outside Southern California');
                    setIsLocating(false);
                    return;
                }
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    const formattedAddress = (status === 'OK' && results && results[0])
                        ? results[0].formatted_address
                        : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                    const placeId = (status === 'OK' && results && results[0]) ? results[0].place_id || '' : '';

                    setStartingLat(lat);
                    setStartingLng(lng);
                    setStartingAddress(formattedAddress);
                    setSearchInput(formattedAddress);
                    setStartingPlaceId(placeId);
                    setHasStartingPoint(true);

                    // Clear existing route
                    setWaypoints([]);
                    setSnappedPath([]);
                    setTotalDistanceMeters(0);
                    setTotalDistanceText('');
                    setIsValidated(false);

                    if (map) {
                        map.panTo({ lat, lng });
                        map.setZoom(ROUTE_DRAWING_ZOOM);
                    }
                    setIsLocating(false);
                });
            },
            (error) => {
                console.warn('Geolocation error:', error.message);
                setIsLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, [map]);

    // Calculate snapped route using Directions API
    const calculateSnappedRoute = useCallback(async (points: RouteWaypoint[]) => {
        if (!directionsServiceRef.current || points.length < 2) {
            setSnappedPath([]);
            setTotalDistanceMeters(0);
            setTotalDistanceText('');
            return;
        }

        setIsCalculatingRoute(true);

        try {
            const sortedPoints = [...points].sort((a, b) => a.order - b.order);
            const origin = { lat: sortedPoints[0].latitude, lng: sortedPoints[0].longitude };
            const destination = { lat: sortedPoints[sortedPoints.length - 1].latitude, lng: sortedPoints[sortedPoints.length - 1].longitude };

            // Create waypoints for intermediate points
            const intermediateWaypoints = sortedPoints.slice(1, -1).map(p => ({
                location: { lat: p.latitude, lng: p.longitude },
                stopover: false
            }));

            const request: google.maps.DirectionsRequest = {
                origin,
                destination,
                waypoints: intermediateWaypoints,
                travelMode: google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false
            };

            directionsServiceRef.current.route(request, (result, status) => {
                if (status === 'OK' && result && result.routes[0]) {
                    const route = result.routes[0];

                    // Extract all points from the route path
                    const path: LatLng[] = [];
                    route.legs.forEach(leg => {
                        leg.steps.forEach(step => {
                            step.path.forEach(point => {
                                path.push({ lat: point.lat(), lng: point.lng() });
                            });
                        });
                    });

                    // Calculate total distance
                    let totalMeters = 0;
                    route.legs.forEach(leg => {
                        if (leg.distance) {
                            totalMeters += leg.distance.value;
                        }
                    });

                    // Format distance text
                    let distanceText = '';
                    if (totalMeters >= 1000) {
                        distanceText = (totalMeters / 1000).toFixed(1) + ' km';
                    } else {
                        distanceText = totalMeters + ' m';
                    }

                    setSnappedPath(path);
                    setTotalDistanceMeters(totalMeters);
                    setTotalDistanceText(distanceText);
                } else {
                    console.warn('Directions request failed:', status);
                    // Fallback: draw straight lines between points
                    const fallbackPath = sortedPoints.map(p => ({ lat: p.latitude, lng: p.longitude }));
                    setSnappedPath(fallbackPath);
                }
                setIsCalculatingRoute(false);
            });
        } catch (error) {
            console.error('Error calculating route:', error);
            setIsCalculatingRoute(false);
        }
    }, []);

    // Handle map click to add waypoint or set starting point
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        if (!isWithinSoCalBounds(lat, lng)) return;

        // If no starting point, use clicked location as starting point
        if (!hasStartingPoint) {
            // Reverse geocode to get address
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const formattedAddress = results[0].formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                    const placeId = results[0].place_id || '';

                    setStartingLat(lat);
                    setStartingLng(lng);
                    setStartingAddress(formattedAddress);
                    setSearchInput(formattedAddress);
                    setStartingPlaceId(placeId);
                    setHasStartingPoint(true);

                    // Zoom in to road level
                    if (map) {
                        map.panTo({ lat, lng });
                        map.setZoom(ROUTE_DRAWING_ZOOM);
                    }
                } else {
                    // Fallback: use coordinates as address
                    const coordAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                    setStartingLat(lat);
                    setStartingLng(lng);
                    setStartingAddress(coordAddress);
                    setSearchInput(coordAddress);
                    setStartingPlaceId('');
                    setHasStartingPoint(true);

                    if (map) {
                        map.panTo({ lat, lng });
                        map.setZoom(ROUTE_DRAWING_ZOOM);
                    }
                }
            });
            return;
        }

        // Add waypoint if we have a starting point
        if (waypoints.length >= MAX_WAYPOINTS) return;

        const newWaypoint: RouteWaypoint = {
            latitude: lat,
            longitude: lng,
            order: waypoints.length + 1
        };

        const newWaypoints = [...waypoints, newWaypoint];
        setWaypoints(newWaypoints);
        setIsValidated(false);

        // Calculate route if we have enough points
        if (newWaypoints.length >= MIN_WAYPOINTS) {
            calculateSnappedRoute(newWaypoints);
        }
    }, [hasStartingPoint, waypoints, calculateSnappedRoute, map]);

    // Remove last waypoint
    const handleUndo = useCallback(() => {
        if (waypoints.length === 0) return;

        const newWaypoints = waypoints.slice(0, -1);
        setWaypoints(newWaypoints);
        setIsValidated(false);

        if (newWaypoints.length >= MIN_WAYPOINTS) {
            calculateSnappedRoute(newWaypoints);
        } else {
            setSnappedPath([]);
            setTotalDistanceMeters(0);
            setTotalDistanceText('');
        }
    }, [waypoints, calculateSnappedRoute]);

    // Clear all waypoints
    const handleClearRoute = useCallback(() => {
        setWaypoints([]);
        setSnappedPath([]);
        setTotalDistanceMeters(0);
        setTotalDistanceText('');
        setIsValidated(false);
    }, []);

    // Validate route (placeholder for now)
    const handleValidateRoute = useCallback(() => {
        if (waypoints.length >= MIN_WAYPOINTS) {
            setIsValidated(true);
        }
    }, [waypoints]);

    // Handle confirm
    const handleConfirm = useCallback(() => {
        if (hasStartingPoint && waypoints.length >= MIN_WAYPOINTS) {
            const config: DrawRoutesConfig = {
                startingPoint: {
                    latitude: startingLat,
                    longitude: startingLng,
                    address: startingAddress,
                    placeId: startingPlaceId || undefined
                },
                waypoints,
                snappedPath,
                totalDistanceMeters: totalDistanceMeters || undefined,
                totalDistanceText: totalDistanceText || undefined,
                isValidated,
                validatedAt: isValidated ? new Date().toISOString() : undefined
            };
            onConfirm(config);
        }
    }, [hasStartingPoint, waypoints, startingLat, startingLng, startingAddress, startingPlaceId, snappedPath, totalDistanceMeters, totalDistanceText, isValidated, onConfirm]);

    const canConfirm = hasStartingPoint && waypoints.length >= MIN_WAYPOINTS;

    // Map container style - adjusts based on fullscreen mode
    const mapContainerStyle = {
        width: '100%',
        height: isFullscreen ? 'calc(100vh - 300px)' : '400px',
        borderRadius: '12px',
        minHeight: isFullscreen ? '400px' : '400px'
    };

    // Styles
    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: isFullscreen ? '0' : '24px',
            boxSizing: 'border-box' as const
        },
        modal: {
            background: '#ffffff',
            borderRadius: isFullscreen ? 0 : 20,
            width: '100%',
            maxWidth: isFullscreen ? '100%' : '760px',
            height: isFullscreen ? '100vh' : 'auto',
            maxHeight: isFullscreen ? '100vh' : '92vh',
            overflow: 'auto',
            boxShadow: isFullscreen ? 'none' : '0 24px 64px rgba(15, 23, 42, 0.25)'
        },
        header: {
            background: '#ffffff',
            padding: '16px 24px',
            position: 'relative' as const,
            borderRadius: isFullscreen ? '0' : '20px 20px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e5e7eb',
            minHeight: '52px'
        },
        headerActions: {
            display: 'flex',
            gap: '6px'
        },
        iconButton: {
            background: '#f3f4f6',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            transition: 'background 0.15s'
        },
        title: {
            color: '#0f172a',
            fontSize: '17px',
            fontWeight: 700,
            margin: 0,
            letterSpacing: '-0.3px'
        },
        body: {
            padding: '24px'
        },
        searchContainer: {
            position: 'absolute' as const,
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 10
        },
        searchInputWrapper: {
            position: 'relative' as const
        },
        searchInput: {
            width: '100%',
            padding: '12px 42px 12px 40px',
            fontSize: '14px',
            border: 'none',
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: '#fff',
            boxSizing: 'border-box' as const,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        },
        searchIcon: {
            position: 'absolute' as const,
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#5f6368',
            zIndex: 1
        },
        locationButton: {
            position: 'absolute' as const,
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#5f6368',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            zIndex: 1
        },
        mapContainer: {
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '16px',
            border: '1px solid #e5e7eb',
            position: 'relative' as const
        },
        instructionBar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#1e40af'
        },
        routeActions: {
            display: 'flex',
            gap: '8px'
        },
        actionButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: 500,
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        undoButton: {
            background: '#f3f4f6',
            color: '#374151'
        },
        clearButton: {
            background: '#fee2e2',
            color: '#dc2626'
        },
        routeInfoPanel: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '16px',
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
        },
        infoItem: {
            textAlign: 'center' as const
        },
        infoLabel: {
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '4px'
        },
        infoValue: {
            fontSize: '18px',
            fontWeight: 600,
            color: '#1f2937'
        },
        validateButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            fontWeight: 600,
            background: isValidated ? '#059669' : 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            marginBottom: '20px',
            transition: 'all 0.15s',
            boxShadow: isValidated ? 'none' : '0 6px 18px rgba(99, 102, 241, 0.35)'
        },
        footer: {
            padding: '16px 24px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            borderTop: '1px solid #e5e7eb'
        },
        cancelButton: {
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            color: '#111827',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s'
        },
        confirmButton: {
            background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
            border: 'none',
            color: '#ffffff',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            transition: 'all 0.15s'
        },
        confirmButtonDisabled: {
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            border: 'none',
            color: '#ffffff',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'not-allowed',
            boxShadow: 'none'
        },
        loadingContainer: {
            width: '100%',
            height: isFullscreen ? 'calc(100vh - 300px)' : '400px',
            minHeight: isFullscreen ? '400px' : '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f4f6',
            borderRadius: '12px'
        },
        errorNotice: {
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#dc2626'
        },
    };

    // Create custom marker icon for starting point (green "S")
    const createStartMarkerIcon = () => {
        if (typeof google !== 'undefined') {
            return {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: '#10b981',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
            };
        }
        return undefined;
    };

    // Create custom marker label for waypoints
    const createWaypointLabel = (order: number): google.maps.MarkerLabel => ({
        text: String(order),
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: '12px'
    });

    if (!isOpen) return null;

    // Check if API key is configured
    const hasApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Draw your route</h1>
                    <div style={styles.headerActions}>
                        <button
                            style={styles.iconButton}
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
                            type="button"
                        >
                            {isFullscreen ? <CompressIcon /> : <ExpandIcon />}
                        </button>
                        <button
                            style={styles.iconButton}
                            onClick={onClose}
                            aria-label="Close"
                            type="button"
                        >
                            <TimesIcon />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {!hasApiKey ? (
                        <div style={styles.errorNotice}>
                            <strong>Configuration Required:</strong> Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and restart the development server.
                        </div>
                    ) : (
                        <LoadScript
                            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            libraries={libraries}
                        >
                            {/* Instruction Bar */}
                            {hasStartingPoint && (
                                <div style={styles.instructionBar}>
                                    <span>
                                        {waypoints.length === 0
                                            ? 'Click on the map to add waypoints and draw your route'
                                            : `${waypoints.length} waypoint${waypoints.length !== 1 ? 's' : ''} added${waypoints.length < MIN_WAYPOINTS ? ` (minimum ${MIN_WAYPOINTS} required)` : ''}`
                                        }
                                    </span>
                                    {waypoints.length > 0 && (
                                        <div style={styles.routeActions}>
                                            <button
                                                style={{...styles.actionButton, ...styles.undoButton}}
                                                onClick={handleUndo}
                                                title="Remove last waypoint"
                                            >
                                                <UndoIcon /> Undo
                                            </button>
                                            <button
                                                style={{...styles.actionButton, ...styles.clearButton}}
                                                onClick={handleClearRoute}
                                                title="Clear all waypoints"
                                            >
                                                <TrashIcon /> Clear
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Map with overlaid search */}
                            <div style={styles.mapContainer}>
                                {/* Address Search - overlaid on map */}
                                <div style={styles.searchContainer}>
                                    <div style={styles.searchInputWrapper}>
                                        <div style={styles.searchIcon}>
                                            <SearchIcon />
                                        </div>
                                        <Autocomplete
                                            onLoad={(autocomplete) => {
                                                autocompleteRef.current = autocomplete;
                                                autocomplete.setFields(['formatted_address', 'geometry', 'name', 'place_id']);
                                                autocomplete.setBounds(SOCAL_AUTOCOMPLETE_BOUNDS);
                                                autocomplete.setOptions({ strictBounds: true });
                                            }}
                                            onPlaceChanged={handlePlaceChanged}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Search for a starting address..."
                                                style={styles.searchInput}
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                                onKeyDown={handleSearchKeyDown}
                                            />
                                        </Autocomplete>
                                        <button
                                            style={{
                                                ...styles.locationButton,
                                                ...(isLocating ? { color: '#3b82f6', cursor: 'wait' } : {})
                                            }}
                                            onClick={handleUseCurrentLocation}
                                            disabled={isLocating}
                                            title="Use my current location"
                                            aria-label="Use my current location"
                                            type="button"
                                        >
                                            {isLocating ? (
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '16px',
                                                    height: '16px',
                                                    border: '2px solid #3b82f6',
                                                    borderTopColor: 'transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 0.8s linear infinite'
                                                }} />
                                            ) : (
                                                <CrosshairsIcon />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={{ lat: startingLat, lng: startingLng }}
                                    zoom={hasStartingPoint ? ROUTE_DRAWING_ZOOM : 12}
                                    onClick={handleMapClick}
                                    onLoad={handleMapLoad}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                        clickableIcons: false,
                                        restriction: SOCAL_RESTRICTION
                                    }}
                                >
                                    {/* Starting Point Marker */}
                                    {hasStartingPoint && (
                                        <Marker
                                            position={{ lat: startingLat, lng: startingLng }}
                                            icon={createStartMarkerIcon()}
                                            label={{
                                                text: 'S',
                                                color: '#ffffff',
                                                fontWeight: 'bold',
                                                fontSize: '10px'
                                            }}
                                            title="Starting Point"
                                        />
                                    )}

                                    {/* Waypoint Markers */}
                                    {waypoints.map((wp) => (
                                        <Marker
                                            key={`waypoint-${wp.order}`}
                                            position={{ lat: wp.latitude, lng: wp.longitude }}
                                            label={createWaypointLabel(wp.order)}
                                            icon={{
                                                path: google.maps.SymbolPath.CIRCLE,
                                                scale: 14,
                                                fillColor: '#3b82f6',
                                                fillOpacity: 1,
                                                strokeColor: '#ffffff',
                                                strokeWeight: 3
                                            }}
                                            title={`Waypoint ${wp.order}`}
                                        />
                                    ))}

                                    {/* Snapped Route Polyline */}
                                    {snappedPath.length > 1 && (
                                        <Polyline
                                            path={snappedPath}
                                            options={{
                                                strokeColor: '#3b82f6',
                                                strokeOpacity: 0.8,
                                                strokeWeight: 5
                                            }}
                                        />
                                    )}
                                </GoogleMap>
                            </div>
                        </LoadScript>
                    )}

                    {/* Route Info Panel */}
                    {hasStartingPoint && waypoints.length >= MIN_WAYPOINTS && (
                        <>
                            <div style={styles.routeInfoPanel}>
                                <div style={styles.infoItem}>
                                    <div style={styles.infoLabel}>Starting Point</div>
                                    <div style={{...styles.infoValue, fontSize: '14px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 auto'}}>
                                        {startingAddress}
                                    </div>
                                </div>
                                <div style={styles.infoItem}>
                                    <div style={styles.infoLabel}>Waypoints</div>
                                    <div style={styles.infoValue}>{waypoints.length}</div>
                                </div>
                                <div style={styles.infoItem}>
                                    <div style={styles.infoLabel}>Total Distance</div>
                                    <div style={styles.infoValue}>
                                        {isCalculatingRoute ? '...' : (totalDistanceText || '-')}
                                    </div>
                                </div>
                            </div>

                            {/* Validate Route Button */}
                            <button
                                style={styles.validateButton}
                                onClick={handleValidateRoute}
                                disabled={isValidated}
                            >
                                {isValidated ? (
                                    <>
                                        <CheckCircleIcon /> Route Validated
                                    </>
                                ) : (
                                    'Validate Route'
                                )}
                            </button>
                        </>
                    )}

                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        style={canConfirm ? styles.confirmButton : styles.confirmButtonDisabled}
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                    >
                        Confirm Route
                    </button>
                </div>
            </div>
        </div>
    );
};

export { DrawRouteMapModal };
